// Price/size/beds/baths/parking ranges are no longer computed here — the
// server returns them as `rollup` on every project (both list and detail
// reads), computed from that project's floor plans server-side. See
// docs/real-estate/be/10-api-reference.md §A2's `rollup` object and FE
// handoff §4.1 ("computed rollup — never typed"). This function used to
// compute the same thing client-side from a FE-only mock; kept out now
// that a real field exists.

// An emptied number input under v-model.number holds '' — the API expects
// null (no value), not an empty string.
export function emptyToNull(value) {
  return value === '' || value === undefined ? null : value;
}

export function money(n, cur) {
  return `${cur || 'RM'} ${Number(n).toLocaleString('en-MY')}`;
}

export function moneyShort(n, cur) {
  const prefix = cur || 'RM';
  if (n >= 1e6)
    return prefix + (n / 1e6).toFixed(2).replace(/\.?0+$/, '') + 'm';
  // Below 1,000 the "k" form rounds to "RM0k"/"RM1k" — show the real amount.
  if (n < 1000) return prefix + Number(n).toLocaleString('en-MY');
  return prefix + Math.round(n / 1000) + 'k';
}

// A floor plan's size is a number in its own unit (`built_up_size` +
// `built_up_unit`, up to 2 decimals) — this only formats, never converts.
// Whole numbers show without decimals ("1,270"), others keep up to two.
export function formatSize(value) {
  return Number(value).toLocaleString('en-MY', { maximumFractionDigits: 2 });
}

// Short label for a size unit code ("sqft", "sqm", "sq yd", ...), falling
// back to the raw code for a unit this build doesn't know yet.
export function sizeUnitLabel(vm, unit) {
  const key = `REAL_ESTATE.SIZE_UNITS.${String(unit).toUpperCase()}`;
  return vm.$te(key) ? vm.$t(key) : unit;
}

// Formats a project's `rollup.size_min`/`size_max` into a plain "min–max"
// (or a single value when they're equal) string, rounded to whole units —
// null when there are no floor plans to size from. The unit is
// `rollup.size_unit` (sqft when the floor plans mix units). Shared by
// ProjectCard.vue and ProjectsTable.vue so the two don't drift; callers wrap
// the result with the CARD.SIZE_RANGE i18n key themselves.
export function sizeRange(rollup) {
  const { size_min: min, size_max: max } = rollup;
  if (min == null) return null;
  const minR = Math.round(min).toLocaleString('en-MY');
  const maxR = Math.round(max).toLocaleString('en-MY');
  return minR === maxR ? minR : `${minR}–${maxR}`;
}

// A brochure's title, or its file name when it has none (the API sends
// `title: null` when the user gave no title).
export function brochureName(brochure) {
  return brochure.title || brochure.file_name;
}

// Brochures are PDF, JPG or PNG — only the images get a thumbnail.
export function isImageBrochure(brochure) {
  return String(brochure.content_type || '').startsWith('image/');
}

// An appointment keeps its project's and floor plan's frozen name after
// either is deleted (portal delete or a Replace import), flagged with
// `deleted` (docs/real-estate/be/13-fe-update-2026-09-30.md §3). While the
// project is deleted, reschedule and agent changes return 409
// property_deleted; a deleted floor plan alone blocks nothing.
export function isProjectDeleted(appointment) {
  return !!(appointment.property && appointment.property.deleted);
}
export function isFloorPlanDeleted(appointment) {
  return !!(appointment.unit && appointment.unit.deleted);
}

// Deterministic string -> [0, length) bucket, used for the project card's
// decorative gradient placeholder (ProjectCard.vue) when there's no cover
// image yet. Property ids are UUID-shaped strings now, not the small
// sequential numbers the old `id % HUES.length` trick relied on.
export function hashIndex(str, length) {
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = hash * 31 + str.charCodeAt(i);
  }
  return hash % length;
}

// Pure string math on a plain "HH:MM" — appointment times are shown
// exactly as the server returns them in its `timezone` field, never
// parsed into a Date/converted to the browser's local zone
// (docs/real-estate/be/10-api-reference.md §0.6).
export function formatTime12h(hhmm) {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const period = h < 12 ? 'AM' : 'PM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
}

// day_of_week 0 = Monday … 6 = Sunday, same convention as OpeningHoursModal.
const DAY_ABBR = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Formats a flat `[{day_of_week, start_time, end_time}]` list (Layer 0 or
// Layer 3 working hours) into a "Mon–Fri 10:00–18:00" summary, matching the
// BE's own `opening_hours_text` (docs/real-estate/be/10-api-reference.md
// §A7 — e.g. "Mon–Tue 10:00–13:00, 14:00–18:00" for a split shift). Groups
// consecutive days that share the exact same ordered list of shifts; a day
// with no rows is closed and omitted entirely.
export function hoursText(rows) {
  const byDay = DAY_ABBR.map((_, dayOfWeek) =>
    rows
      .filter(row => row.day_of_week === dayOfWeek)
      .slice()
      .sort((a, b) => (a.start_time < b.start_time ? -1 : 1))
      .map(row => `${row.start_time}–${row.end_time}`)
      .join(', ')
  );

  const groups = [];
  byDay.forEach((signature, dayOfWeek) => {
    const last = groups[groups.length - 1];
    if (last && last.signature === signature) {
      last.end = dayOfWeek;
    } else {
      groups.push({ signature, start: dayOfWeek, end: dayOfWeek });
    }
  });

  return groups
    .filter(group => group.signature)
    .map(group =>
      group.start === group.end
        ? `${DAY_ABBR[group.start]} ${group.signature}`
        : `${DAY_ABBR[group.start]}–${DAY_ABBR[group.end]} ${group.signature}`
    )
    .join(', ');
}

// Validates a TimeRangeFields value where both-null means "all day" —
// returns the i18n key to show, or null when the range is fine. The BE
// 422s when only one time is set, so blanks must be caught here.
export function timeRangeErrorKey({ start_time: start, end_time: end }) {
  if (start === null && end === null) return null;
  if (!start || !end) return 'REAL_ESTATE.FORM.TIMES_REQUIRED';
  if (end <= start) return 'REAL_ESTATE.ERRORS.INVALID_TIME_RANGE';
  return null;
}

// Strips server-added fields (`id`, …) from weekly-hours rows so they can
// be compared and PUT back — the request rows only take these three.
export function toHourRows(rows) {
  return rows.map(({ day_of_week, start_time, end_time }) => ({
    day_of_week,
    start_time,
    end_time,
  }));
}

// Triggers a browser download of a Blob (e.g. the import template fetched through the proxy, which
// drops the server's Content-Disposition, so the name is set here).
export function downloadBlob(filename, blob) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// Property agents aren't linked to portal (Chatwoot) users on the BE, so a
// property agent "has portal access" when a user of this account has the
// same email. Returns that user, or null.
export function findPortalUser(agent, users) {
  const email = String((agent && agent.email) || '')
    .trim()
    .toLowerCase();
  if (!email) return null;
  return (
    (users || []).find(
      user =>
        String(user.email || '')
          .trim()
          .toLowerCase() === email
    ) || null
  );
}

// 'active' (the user has accepted the invite), 'pending' (invited, not yet
// confirmed) or 'none'.
export function portalAccessStatus(agent, users) {
  const user = findPortalUser(agent, users);
  if (!user) return 'none';
  return user.confirmed ? 'active' : 'pending';
}
